import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../App/hooks";
import {
  IEditManualOrderFormValues,
  IFailedManualStepOrderEditItem,
  IPreviewEditVolumeResult,
  ManualScheduleMode,
} from "../../Interfaces/manualInterfaces";
import { StepInstrumentType, VolumeCalculationType } from "../../Interfaces/stepInterfaces";
import StaticDropdown from "../../Components/Dropdown/StaticDropdown";
import {
  setManualEditRowValues,
  setManualOrderTableFetchDataFlag,
} from "../../Features/Manual/manualSlice";
import TextInput from "../../Components/TextInput";
import { addSeperatorToInput } from "../../helper";
import Button from "../../Components/Button";
import Modal from "../../Components/Modal/Modal";
import FullScreenLoading from "../../Components/Loading/FullScreenLoading";
import {
  editManualOrder,
  getSimilarManualOrders,
  previewEditManualOrderVolume,
} from "../../Requests/manualRequests";
import { useDebouncer } from "src/Hooks/useDebouncer";
import { toast } from "react-toastify";
import TabBox from "../../Components/Tabbox/TabBox";
import DynamicMultiSelectDropdown from "../../Components/Dropdown/DynamicMultiSelectDropdown";
import EditFailedOrdersTable from "../../Tables/Manual/EditFailedOrdersTable";
import { CheckBox, TimePicker } from "src/Components";
import {
  getManualScheduledExecutionTime,
  getManualTimePickerValue,
} from "./AddOrderBox/manualScheduleUtils";

const EditOrder = () => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);

  const editOrderRowValues = useAppSelector(
    (store) => store.manual.editOrderRowValues
  );

  const [formValues, setFormValues] = useState<IEditManualOrderFormValues>(
    {} as IEditManualOrderFormValues
  );

  const [failedOrders, setFailedOrders] = useState<
    IFailedManualStepOrderEditItem[] | null
  >(null);
  const [volumeCalculationTouched, setVolumeCalculationTouched] =
    useState(false);
  const [volumePreview, setVolumePreview] =
    useState<IPreviewEditVolumeResult | null>(null);
  const [volumePreviewLoading, setVolumePreviewLoading] = useState(false);

  useEffect(() => {
    if (editOrderRowValues) {
      setFormValues({
        manualStepOrderId: editOrderRowValues.id,
        fromPrice: editOrderRowValues.firstStep,
        stepCount: editOrderRowValues.stepCount,
        toPrice: editOrderRowValues.lastStep,
        totalVolume: null,
        manualStepOrderIds: null,
        applyToAll: null,
        autoSellFund: !!editOrderRowValues.autoSellFund,
        scheduleMode: null,
        scheduledExecutionTime: editOrderRowValues.scheduledExecutionTimeRaw || null,
        // Pre-fill with the method+value the order was actually placed with (falls back to a plain
        // fixed count for older orders recorded before this was tracked).
        volumeCalculationType:
          editOrderRowValues.instrumentType === StepInstrumentType.Future
            ? VolumeCalculationType.FixedAmount
            : editOrderRowValues.volumeCalculationType ?? VolumeCalculationType.FixedAmount,
        volumeCalculationValue:
          editOrderRowValues.volumeCalculationValue ??
          editOrderRowValues.totalSize,
      });
      setVolumeCalculationTouched(false);
      setVolumePreview(null);
      setFailedOrders(null);
    }
  }, [editOrderRowValues]);

  const changeHandler = (name: string, value: any) => {
    // Functional updater, not `{ ...formValues, [name]: value }` - scheduleModeChangeHandler calls this
    // twice in a row (scheduleMode, then scheduledExecutionTime) within the same event handler, and
    // React batches both setFormValues calls before re-rendering. With the old spread-of-the-closure
    // form, both calls captured the same pre-click `formValues`, so the second call's spread silently
    // discarded the first call's change instead of building on it.
    if (
      name === "volumeCalculationType" ||
      name === "volumeCalculationValue"
    ) {
      setVolumeCalculationTouched(true);
    }
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (!formValues.applyToAll) changeHandler("manualStepOrderIds", null);
  }, [formValues.applyToAll]);

  // Live "≈ N shares" preview for a percentage/derived volume method. Debounced so it doesn't fire
  // on every keystroke, and only once the trader has actually touched the method/value in this
  // session - if untouched, submit keeps the order's current volume untouched too (see onSendClick),
  // so previewing it would suggest a recalculation that won't actually happen.
  const debouncedVolumeCalculationType = useDebouncer(
    formValues.volumeCalculationType,
    500,
  );
  const debouncedVolumeCalculationValue = useDebouncer(
    formValues.volumeCalculationValue,
    500,
  );
  const debouncedFromPrice = useDebouncer(formValues.fromPrice, 500);
  const debouncedToPrice = useDebouncer(formValues.toPrice, 500);
  const volumeCalculationChanged =
    volumeCalculationTouched &&
    editOrderRowValues != null &&
    (formValues.volumeCalculationType !==
      editOrderRowValues.volumeCalculationType ||
      Number(formValues.volumeCalculationValue) !==
        editOrderRowValues.volumeCalculationValue);

  useEffect(() => {
    const value = Number(debouncedVolumeCalculationValue);

    if (
      !volumeCalculationChanged ||
      !editOrderRowValues ||
      debouncedVolumeCalculationType == null ||
      debouncedVolumeCalculationType === VolumeCalculationType.FixedAmount ||
      !value ||
      value <= 0
    ) {
      setVolumePreview(null);
      return;
    }

    let cancelled = false;
    setVolumePreviewLoading(true);
    previewEditManualOrderVolume({
      manualStepOrderId: editOrderRowValues.id,
      volumeCalculationType: debouncedVolumeCalculationType,
      volumeCalculationValue: value,
      fromPrice: Number(debouncedFromPrice) || editOrderRowValues.firstStep,
      toPrice: Number(debouncedToPrice) || editOrderRowValues.lastStep,
    })
      .then((resp) => {
        if (!cancelled) setVolumePreview(resp);
      })
      .catch(() => {
        if (!cancelled)
          setVolumePreview({
            success: false,
            volume: null,
            totalVolume: null,
            remainedVolume: null,
            msg: null,
          });
      })
      .finally(() => {
        if (!cancelled) setVolumePreviewLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    volumeCalculationChanged,
    debouncedVolumeCalculationType,
    debouncedVolumeCalculationValue,
    debouncedFromPrice,
    debouncedToPrice,
    editOrderRowValues,
  ]);

  const checkboxChangeHandler = (name: string, checked: boolean) => {
    changeHandler(name, checked);
  };

  const scheduleModeChangeHandler = (name: string, _: any, value: number) => {
    changeHandler(name, value);
    if (
      value === ManualScheduleMode.Scheduled &&
      !formValues.scheduledExecutionTime
    ) {
      changeHandler(
        "scheduledExecutionTime",
        editOrderRowValues?.scheduledExecutionTimeRaw || null,
      );
    }
  };

  const timeChangeHandler = (
    name: string,
    value: { timeString: string; ISOString: string | null },
  ) => {
    // Changing the picker is an explicit scheduling edit. Without this, an order that was already
    // scheduled kept scheduleMode=null ("leave unchanged") and the API ignored the new time.
    changeHandler("scheduleMode", ManualScheduleMode.Scheduled);
    changeHandler(name, getManualScheduledExecutionTime(value));
  };

  const onSendClick = () => {
    if (!editOrderRowValues || loading) return;

    const requestedValue = Number(formValues.volumeCalculationValue);

    setLoading(true);
    editManualOrder({
      ...formValues,
      totalVolume: null,
      // These fields are commands, not a snapshot: null means "keep the current volume". Sending
      // the pre-filled values on every price/schedule edit would re-run percentage-based sizing.
      volumeCalculationType: volumeCalculationTouched
        ? formValues.volumeCalculationType
        : null,
      volumeCalculationValue:
        volumeCalculationTouched && requestedValue > 0 ? requestedValue : null,
    })
      .then((resp) => {
        if (resp.failedOrders?.length) {
          setFailedOrders(resp.failedOrders);
          dispatch(setManualOrderTableFetchDataFlag(true));
          return;
        }

        if (!resp.data) {
          toast.error(resp.msg);
          return;
        }

        toast.success(resp.msg);
        dispatch(setManualEditRowValues(null));
        dispatch(setManualOrderTableFetchDataFlag(true));
      })
      .catch((e) =>
        toast.error(
          e?.response?.data?.value ||
            e?.response?.data?.msg ||
            "خطا در ویرایش سفارش",
        ),
      )
      .finally(() => setLoading(false));
  };

  const checkIsBtnDisabled = () => {
    if (!formValues.fromPrice || !formValues.toPrice || !formValues.stepCount)
      return true;
    // Only a plain fixed count is directly comparable to filledVolume client-side; percentage-based
    // methods depend on live portfolio data the server has to resolve, so their "can't drop below
    // what's done" check happens there instead (ManualOrderEditPolicy.ResolveVolume).
    if (
      isFixedAmountVolume &&
      formValues.volumeCalculationValue != null &&
      Number(formValues.volumeCalculationValue) <
        (editOrderRowValues?.filledVolume || 0)
    )
      return true;
    if (
      formValues.scheduleMode === ManualScheduleMode.Scheduled &&
      !formValues.scheduledExecutionTime
    )
      return true;
    return false;
  };

  const onFailedOrdersClose = () => {
    setFailedOrders(null);
    dispatch(setManualEditRowValues(null));
  };

  const getOrderSideClassname = (orderSide: string) => {
    switch (orderSide) {
      case "خرید":
        return "BuyCssClass";
      case "فروش":
        return "SellCssClass";
      default:
        break;
    }
  };

  const onCancelClick = () => {
    dispatch(setManualEditRowValues(null));
  };

  const getTabBoxChildren = () => [
    {
      title: editOrderRowValues?.sahraName || "نام اکانت",
      index: 1,
      value: false,
    },
    {
      title: "همه سفارشات مشابه",
      index: 2,
      value: true,
    },
  ];

  const getTabBoxActiveTab = () => {
    if (formValues.applyToAll) return 2;
    return 1;
  };

  const getScheduleTabChildren = () => [
    {
      title: "فوری",
      index: 1,
      value: ManualScheduleMode.Immediate,
    },
    {
      title: "زمان‌بندی‌شده",
      index: 2,
      value: ManualScheduleMode.Scheduled,
    },
  ];

  const volumeCalculationTypeOptions = [
    { id: VolumeCalculationType.FixedAmount, name: "مقدار ثابت" },
    { id: VolumeCalculationType.PortfolioPercent, name: "% از پورتفو" },
    { id: VolumeCalculationType.AssetPercent, name: "% از دارایی" },
    {
      id: VolumeCalculationType.TargetPortfolioPercent,
      name: "% هدف از پورتفو",
    },
    { id: VolumeCalculationType.BuyingPowerPercent, name: "% از قدرت خرید" },
    {
      id: VolumeCalculationType.FixedCapitalAmount,
      name: "سرمایه درگیر (مبلغ) ثابت",
    },
  ];

  const volumeValueLabel =
    volumeCalculationTypeOptions.find(
      (item) => item.id === formValues.volumeCalculationType,
    )?.name ?? "حجم";

  const isFixedAmountVolume =
    formValues.volumeCalculationType === VolumeCalculationType.FixedAmount ||
    formValues.volumeCalculationType == null;

  // Nothing chosen yet in this session (scheduleMode still null) shows the order's current
  // state; an explicit click always overrides it from here on.
  const displayScheduleMode =
    formValues.scheduleMode ??
    (editOrderRowValues?.scheduledExecutionTimeRaw
      ? ManualScheduleMode.Scheduled
      : ManualScheduleMode.Immediate);

  const getScheduleTabActiveTab = () =>
    displayScheduleMode === ManualScheduleMode.Scheduled ? 2 : 1;

  if (!editOrderRowValues) return null;

  const formUI = (
    <div className="flex flex-col justify-start items-start gap-3 whitespace-nowrap text-xs">
      {loading ? <FullScreenLoading /> : null}
      <div className="flex flex-row justify-between items-center w-full">
        <div className="text-sm font-bold">ویرایش</div>
        <div className="flex flex-row justify-start items-center gap-3">
          <div>نماد:</div>
          <div>{editOrderRowValues.namad}</div>
        </div>
        <div className="flex flex-row justify-start items-center gap-3">
          <div>جهت سفارش:</div>
          <div className={getOrderSideClassname(editOrderRowValues.orderSide)}>
            {editOrderRowValues.orderSide}
          </div>
        </div>
        <div className="flex flex-row justify-start items-center gap-1 text-xs">
          <div>موجودی پورتفو:</div>
          <div>{editOrderRowValues.portfoAmount}</div>
        </div>
      </div>

      <div className="flex flex-row w-full justify-between items-center gap-3">
        <TabBox
          children={getTabBoxChildren()}
          activeTab={getTabBoxActiveTab()}
          onChange={(name, _, value) => changeHandler(name, value)}
          name="applyToAll"
        />
      </div>
      {formValues.applyToAll ? (
        <div className="flex flex-row w-full justify-start items-center gap-3">
          <DynamicMultiSelectDropdown
            dropdown={{
              name: "manualStepOrderIds",
              placeholder: "همه سفارشات مشابه",
            }}
            getData={() => getSimilarManualOrders(formValues.manualStepOrderId)}
            onChange={changeHandler}
            value={formValues.manualStepOrderIds || []}
          />
        </div>
      ) : null}

      <div className="w-full flex flex-row justify-between items-center gap-3">
        <div className="flex flex-row justify-start items-center gap-3 w-1/2">
          <div className="flex flex-row justify-start items-center gap-1 w-[90%] text-xs">
            <div>ابتدای بازه:</div>
            <div>
              {addSeperatorToInput(editOrderRowValues.firstStep, "price")}
            </div>
          </div>
          <TextInput
            label=""
            name="fromPrice"
            onChange={changeHandler}
            value={formValues.fromPrice?.toString()}
            kind="price"
          />
        </div>

        <div className="flex flex-row justify-start items-center gap-3 w-1/2">
          <div className="flex flex-row justify-start items-center gap-1 w-[90%] text-xs">
            <div>انتهای بازه:</div>
            <div>
              {addSeperatorToInput(editOrderRowValues.lastStep, "price")}
            </div>
          </div>
          <TextInput
            label=""
            name="toPrice"
            onChange={changeHandler}
            value={formValues.toPrice?.toString()}
            kind="price"
          />
        </div>
      </div>
      <div className="w-full flex flex-row justify-between items-center gap-3">
        <div className="flex flex-row justify-start items-center gap-3 w-1/2">
          <div className="flex flex-row justify-start items-center gap-1 w-[90%] text-xs">
            <div>پلکان:</div>
            <div>{editOrderRowValues.stepCount}</div>
          </div>
          <TextInput
            label=""
            name="stepCount"
            onChange={changeHandler}
            value={formValues.stepCount?.toString()}
            kind="price"
          />
        </div>
        <div className="flex flex-row justify-start items-center gap-1 w-1/2 text-xs">
          <div>حجم اولیه:</div>
          <div className="ltr">
            {addSeperatorToInput(editOrderRowValues.totalSize, "price")}
          </div>
        </div>
      </div>
      <div className="w-full flex flex-row justify-between items-center gap-3">
        <div className="flex flex-row justify-start items-center gap-1 w-1/2 text-xs">
          <div>حجم باقی مانده:</div>
          <div className="ltr">
            {addSeperatorToInput(
              editOrderRowValues.totalSize -
                (editOrderRowValues.filledVolume || 0),
              "price"
            )}
          </div>
        </div>
      </div>
      <div className="w-full flex flex-row justify-between items-center gap-3">
        <StaticDropdown
          dropdown={{
            name: "volumeCalculationType",
            placeholder: "روش تعیین حجم",
          }}
          onChange={changeHandler}
          value={formValues.volumeCalculationType}
          options={editOrderRowValues?.instrumentType === StepInstrumentType.Future
            ? volumeCalculationTypeOptions.filter(item => item.id === VolumeCalculationType.FixedAmount)
            : volumeCalculationTypeOptions}
        />
        <TextInput
          label={volumeValueLabel}
          name="volumeCalculationValue"
          onChange={changeHandler}
          value={formValues.volumeCalculationValue?.toString() || ""}
          kind="priceWithDecimal"
        />
      </div>
      {volumeCalculationChanged &&
      formValues.volumeCalculationType !== VolumeCalculationType.FixedAmount ? (
        <div className="w-full flex flex-col items-end gap-1 text-[11px] text-gray-500">
          {volumePreviewLoading ? (
            "در حال محاسبه..."
          ) : volumePreview?.success && volumePreview.totalVolume != null ? (
            <>
              {formValues.volumeCalculationType ===
                VolumeCalculationType.TargetPortfolioPercent &&
                volumePreview.volume != null ? (
                <div>
                  حجم لازم تا هدف: ≈ {addSeperatorToInput(volumePreview.volume, "price")} سهم
                </div>
              ) : null}
              <div>
                حجم کل سفارش پس از ویرایش: ≈ {addSeperatorToInput(volumePreview.totalVolume, "price")} سهم
              </div>
              {formValues.volumeCalculationType !==
                VolumeCalculationType.TargetPortfolioPercent &&
                volumePreview.remainedVolume != null ? (
                <div>
                  حجم باز پس از ویرایش: ≈ {addSeperatorToInput(volumePreview.remainedVolume, "price")} سهم
                </div>
              ) : null}
            </>
          ) : volumePreview && !volumePreview.success ? (
            volumePreview.msg || "امکان محاسبه حجم نبود"
          ) : null}
        </div>
      ) : null}
      <div className="w-full flex flex-row justify-between items-center gap-3">
        <TabBox
          children={getScheduleTabChildren()}
          activeTab={getScheduleTabActiveTab()}
          onChange={scheduleModeChangeHandler}
          name="scheduleMode"
        />
        {displayScheduleMode === ManualScheduleMode.Scheduled ? (
          <TimePicker
            name="scheduledExecutionTime"
            placeholder="ساعت اجرا"
            value={getManualTimePickerValue(formValues.scheduledExecutionTime)}
            onChange={timeChangeHandler}
          />
        ) : null}
      </div>
      <div className="w-full flex flex-row justify-between items-center gap-3">
        <CheckBox
          checked={!!formValues.autoSellFund}
          label="فروش خودکار صندوق درآمد ثابت"
          name="autoSellFund"
          onChange={checkboxChangeHandler}
        />
      </div>
      <div className="flex flex-row justify-between items-center w-full">
        <Button
          onClick={onCancelClick}
          text="لغو"
          variant="outlined"
          className="!w-16 !h-6 !text-xs"
        />
        <Button
          onClick={onSendClick}
          text="ارسال"
          variant="contained"
          className="!w-16 !h-6 !text-xs"
          disabled={checkIsBtnDisabled()}
        />
      </div>
    </div>
  );

  if (failedOrders?.length) {
    const failedOrdersUI = (
      <div className="flex flex-col justify-start items-start gap-3 text-xs">
        <div className="text-sm font-bold">سفارش‌های ناموفق</div>
        <EditFailedOrdersTable rows={failedOrders} />
        <Button
          onClick={onFailedOrdersClose}
          text="بستن"
          variant="outlined"
          className="!w-16 !h-6 !text-xs"
        />
      </div>
    );
    return <Modal title="سفارش‌های ناموفق" onClose={onFailedOrdersClose} UI={failedOrdersUI} />;
  }

  return <Modal title="ویرایش سفارش" onClose={onCancelClick} UI={formUI} />;
};

export default EditOrder;
